import { useContext, useMemo } from "react"
import { omit, chain, split, keyBy, isEmpty, compact } from "lodash"
import { useInstrumentsWithQueryOptions, useBaseWithSegment, SELECTED_MODE_VALUES } from "connected-components/dashboard/helpers"
import { useModelMetadata } from "resources/models"
import { OK } from "helpers/data-statuses"
import { createCompositionContributorsQuery } from "gatherers/contributors/composition"
import { createContentSetContributorsQuery } from "gatherers/contributors/content-set"
import { createFactorContentSetQuery } from "gatherers/contributors/factor-content-set"
import { createRiskFactorsQuery } from "gatherers/contributors/factor-risk"
import { createPerformanceFactorsQuery } from "gatherers/contributors/factor-performance"
import { createBetaContributorsQuery } from "gatherers/contributors/beta"
import { createRiskContributorsQuery } from "gatherers/contributors/risk"
import { createExposureContributorsQuery } from "gatherers/contributors/exposure"
import { createPerformanceContributorsQuery } from "gatherers/contributors/performance"
import { createFactorBetaQuery } from "gatherers/contributors/factor-beta"
import { createFactorSensitivityQuery } from "gatherers/contributors/factor-sensitivity"
import { createFactorSensitivityContributorsQuery } from "gatherers/contributors/sensitivities"
import { createMarginalContributionQuery } from "gatherers/contributors/marginalContribution"
import { createForecastContributorsQuery } from "gatherers/contributors/forecast"
import { useIsOpPro, useIsRfrAttributionEnabled } from "resources/account"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { getGatherersFromInstruments } from "../../asset-view/helpers"
import useSecuritySearchCount from "./use-security-search-count"
import { useIntradayMode } from "./use-intraday-mode"

const createCommonQueryOptions = ({ showNotHeld, selectedCategory, instrument, from, to, startDate, endDate, groupBy, contextBase, isEndVsStartComparisonInstrument, ...params }) => {
  const fromDate = from || startDate
  const toDate = to || endDate
  return {
    notHeldRollup: !showNotHeld,
    category: selectedCategory,
    positionSet: instrument,
    from: fromDate,
    to: isEndVsStartComparisonInstrument ? fromDate : toDate,
    groupByOptions: groupBy,
    base: contextBase,
    ...params,
  }
}

function useTemplateQueryFactories() {
  return useMemo(() => ({
    composition: (params) => [
      ...createCompositionContributorsQuery({
        prefix: `composition${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
      ...createCompositionContributorsQuery({
        prefix: `marketValue${params.keySuffix}`,
        scaleFormat: "DEFAULT",
        ...createCommonQueryOptions(params),
      }),
    ],

    contentSet: (params) => {
      const { contentSetId } = params
      return [
        createContentSetContributorsQuery({
          prefix: `${contentSetId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          contentSetId,
        }),
      ]
    },

    factorContentSet: (params) => {
      const { contentSetId } = params
      return [
        createFactorContentSetQuery({
          prefix: `${contentSetId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          contentSetId,
        }),
      ]
    },

    summary: (params) => [
      createPerformanceContributorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions({ ...params, from: params.from || params.computedPerformanceStartDate }),
      }),
      ...(!params.isOpPro ? [
        createRiskContributorsQuery({
          prefix: `risk${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ] : []),
      ...(params.betaFields?.length > 0 ? [
        createBetaContributorsQuery({
          prefix: `beta${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ] : []),
    ],

    exposure: (params) => [
      createExposureContributorsQuery({
        prefix: `exposure${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    risk: (params) => [
      createRiskContributorsQuery({
        prefix: `risk${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    predictedFactorSensitivity: (params) => [
      createFactorSensitivityContributorsQuery({
        prefix: `predictedFactorSensitivity${params.keySuffix}`,
        factorSensitivityType: FACTOR_SENSITIVITY_TYPES.PREDICTED,
        ...createCommonQueryOptions(params),
      }),
    ],

    historicalFactorSensitivity: (params) => [
      createFactorSensitivityContributorsQuery({
        prefix: `historicalFactorSensitivity${params.keySuffix}`,
        factorSensitivityType: FACTOR_SENSITIVITY_TYPES.HISTORICAL,
        ...createCommonQueryOptions(params),
      }),
    ],

    factorRisk: (params) => {
      const { driftStartDate, isEndVsStartComparisonInstrument, from } = params
      const fromDate = isEndVsStartComparisonInstrument ? from : driftStartDate
      return [
        createRiskFactorsQuery({
          prefix: `risk${params.keySuffix}`,
          ...createCommonQueryOptions({ ...params, from: fromDate }),
        }),
        createFactorSensitivityQuery({
          prefix: `sensitivity${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ]
    },

    marginal: (params) => [
      createMarginalContributionQuery({
        prefix: `marginal${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    performance: (params) => [
      createPerformanceContributorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions({ ...params, from: params.from || params.computedPerformanceStartDate }),
      }),
    ],

    factorPerformance: (params) => [
      createPerformanceFactorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    beta: (params) => [
      createBetaContributorsQuery({
        prefix: `beta${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    factorBeta: (params) => [
      createFactorBetaQuery({
        prefix: `beta${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    forecast: (params) => {
      const { forecastId, forecastSecurities } = params
      const isImpliedReturns = forecastId === "impliedReturns"
      let additionalParams = { forecastSecurities: null }
      if (isImpliedReturns && isEmpty(forecastSecurities)) {
        return null
      } else if (isImpliedReturns) {
        additionalParams = { forecastId: null, forecastSecurities }
      }
      return [
        createForecastContributorsQuery({
          prefix: `${forecastId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          ...additionalParams,
        }),
      ]
    },
  }), [])
}

function getContentSetGatherer(contentSetId, options, templateQueryFactories) {
  return templateQueryFactories.contentSet({
    ...options,
    contentSetId,
    selectedCategory: null,
    selectedMetricDisplay: null,
  })
}

function getForecastGatherer(forecastId, options, templateQueryFactories) {
  return templateQueryFactories.forecast({
    ...options,
    forecastId,
  })
}

function getTemplateFactoryForKey({ key, contentSetKeys, forecastKeys, templateQueryFactories }) {
  if (contentSetKeys[key]) {
    return (options) => getContentSetGatherer(key, options, templateQueryFactories)
  }

  if (forecastKeys[key] || key === "impliedReturns") {
    return (options) => getForecastGatherer(key, options, templateQueryFactories)
  }

  return templateQueryFactories[key] || null
}

function computeMetricKeys(selectedMetrics, classifications) {
  const classificationKeys = keyBy(classifications, "id")
  const groupedKeys = chain(selectedMetrics)
    .groupBy((id) => split(id, ".")[0])
    .keys()
    .value()
  return { classificationKeys, groupedKeys }
}

function makeCustomGatherers({ contentSets, forecasts, ...options }, templateQueryFactories) {
  const contentSetKeys = keyBy(contentSets, "id")
  const forecastKeys = keyBy(forecasts, "id")

  const otherQueries = options.keys.flatMap((key) => {
    const templateFactory = getTemplateFactoryForKey({ key, contentSetKeys, forecastKeys, templateQueryFactories })
    return templateFactory ? templateFactory(options) : []
  })

  return otherQueries
}

function getTemplateGatherersOptions(templateOptions, templateType) {
  const isTradeQuery = templateType === "trade"
  const useCompositionQueries = templateOptions.useCompositionQueries ?? true
  const options = omit(templateOptions, "useCompositionQueries")

  return { useCompositionQueries, isTradeQuery, options, templateType }
}

function useTemplateGatherersData(contextData) {
  const { modelId, endDate, startDate, base: contextBase, groupBy, showNotHeld, instrument: original, secondInstrument: rebalanced, comparisonInstrument: comparison, target, selectedMode, isExpanded, normalization, filter, modelMetadata } = contextData

  const instrumentOptions = useMemo(() => ({ isExpanded, normalization, filter }), [isExpanded, normalization, filter])
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison, target }, selectedMode, instrumentOptions)
  const baseWithSegment = useBaseWithSegment(contextBase, filter, normalization)

  const defaultProps = useMemo(() => ({ modelId, contextBase: baseWithSegment, endDate, startDate, groupBy, showNotHeld, modelName: modelMetadata?.name }), [modelId, baseWithSegment, endDate, startDate, groupBy, showNotHeld, modelMetadata?.name])

  return { instrumentsWithQueryOptions, defaultProps }
}

function instrumentIsPortfolio(instrument) {
  return instrument.type === "PORTFOLIO" || instrument.rulesConfiguration?.source?.positionSet?.type === "PORTFOLIO"
}

function createGathererQueries(instrument, keySuffix, templateType, defaultProps, options, useCompositionQueries, templateQueryFactories, isEndVsStartComparisonInstrument) {
  const queries = []

  const { classificationKeys, groupedKeys } = computeMetricKeys(options.selectedMetrics, options?.classifications)

  let needsDescriptors = false
  let needsAttributes = false
  if (templateType === "custom" && options.selectedMetrics) {
    needsDescriptors = groupedKeys.includes("descriptors") || groupedKeys.some(key => classificationKeys[key])
    needsAttributes = groupedKeys.includes("attributes") && instrumentIsPortfolio(instrument)
  }

  if (useCompositionQueries) {
    queries.push(...templateQueryFactories.composition({
      keySuffix,
      instrument,
      isEndVsStartComparisonInstrument,
      ...defaultProps,
      ...options,
      ...(needsDescriptors ? { useDescriptors: true } : {}),
      ...(needsAttributes ? { useAttributes: true } : {}),
    }))
  }

  const keys = groupedKeys.filter(key => !["descriptors", "attributes"].includes(key) && !classificationKeys[key])

  const templateFactory = templateType === "custom" ? (params) => makeCustomGatherers({ ...params, keys }, templateQueryFactories) : templateQueryFactories[templateType]
  if (templateFactory) {
    queries.push(...templateFactory({ keySuffix, instrument, isEndVsStartComparisonInstrument, ...defaultProps, ...options }))
  }

  return compact(queries)
}

export function useTemplateGatherers(context, templateType, templateOptions = {}) {
  const contextData = useContext(context)
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(contextData.modelId)
  const isOpPro = useIsOpPro()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()
  const { useCompositionQueries, options } = useMemo(() => getTemplateGatherersOptions(templateOptions, templateType), [templateOptions, templateType])

  // For trade template, always get both original and rebalanced data to display original data alongside rebalanced data in add trade mode
  const effectiveSelectedMode = templateType === "trade"
    ? SELECTED_MODE_VALUES.rebalancedVsOriginal
    : contextData.selectedMode

  const { instrumentsWithQueryOptions, defaultProps } = useTemplateGatherersData({ ...contextData, modelMetadata, selectedMode: effectiveSelectedMode })
  const templateQueryFactories = useTemplateQueryFactories()
  const { intradayBatchMetadata, latestDate, endDate, isEndVsStartDateComparison, startDate } = contextData
  const [{ isIntradayMode }] = useIntradayMode()

  const isCustomTemplate = templateType == "custom"
  const comparisonCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, instrument: instrumentsWithQueryOptions.comparison, enabled: isCustomTemplate }))
  const rebalancedCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, instrument: instrumentsWithQueryOptions.rebalanced, enabled: isCustomTemplate }))
  const originalCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, enabled: isCustomTemplate }))

  const counts = useMemo(() => ({
    comparison: comparisonCount,
    rebalanced: rebalancedCount,
    original: originalCount,
  }), [comparisonCount, rebalancedCount, originalCount])

  const fullOptions = useMemo(() => ({ ...options, isOpPro, isRfrAttributionEnabled }), [options, isOpPro, isRfrAttributionEnabled])

  return useMemo(() => {
    if (modelMetadataStatus === OK) {
      return getGatherersFromInstruments(instrumentsWithQueryOptions, (instrument, keySuffix, instrumentType) => {
        const instrumentOptions = { ...fullOptions, count: counts[instrumentType] }

        instrumentOptions.shouldUseIntradayQuery = false
        const isEndVsStartComparisonInstrument = isEndVsStartDateComparison && instrumentType === "comparison"
        if (isIntradayMode) {
          if (instrumentType !== "comparison") {
            instrumentOptions.on = endDate
            instrumentOptions.shouldUseIntradayQuery = true

            if (instrumentType === "original" || instrumentType === "rebalanced") {
              instrumentOptions.batchId = intradayBatchMetadata?.id
            }
          } else {
            // we make an exception for end vs start comparison instrument
            // if we're in end vs start comparison
            // if we're in intraday mode
            // if start date is equal to end date, which is today due to being in intraday mode
            // then we can use the intraday query
            if (isEndVsStartComparisonInstrument && startDate === endDate) {
              instrumentOptions.on = startDate
              instrumentOptions.shouldUseIntradayQuery = true
            }
            if (endDate > latestDate) {
              instrumentOptions.to = latestDate
            }
          }
        }
        return createGathererQueries(instrument, keySuffix, templateType, defaultProps, instrumentOptions, useCompositionQueries, templateQueryFactories, isEndVsStartComparisonInstrument)
      })
    }

    return []
  }, [instrumentsWithQueryOptions, templateType, defaultProps, useCompositionQueries, fullOptions, templateQueryFactories, modelMetadataStatus, intradayBatchMetadata, latestDate, endDate, startDate, isEndVsStartDateComparison, isIntradayMode, counts])
}
